import { HttpContextToken, HttpErrorResponse, HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthProvider,
  DataProvider,
  getMessageFromError,
  getTitleFromError,
  IHttpErrorResponse,
  isIUpdateVarErrorsResult,
  LANGUAGE_COOKIE_NAME
} from '@gs/shared';
import { InformationService, LanguageService, SystemService, ToastService } from '@gs/shared/services';
import { TranslateService } from '@ngx-translate/core';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { catchError, tap, throwError } from 'rxjs';

const COOKIE_PATH: string = '/';

/** Lets a request handle its errors itself: no toast and no logout on 401 (e.g. silent background checks) */
export const SKIP_ERROR_HANDLING = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthProvider);
  const cookieServiceSSR = inject(SsrCookieService);
  const initService = inject(InformationService);
  const router = inject(Router);
  const systemService = inject(SystemService);
  const injector = inject(Injector);
  const toastService = inject(ToastService);

  // **Bypass for translation requests**
  if (req.url.includes('/i18n/')) {
    return next(req); // Skip all headers or modifications for translation files
  }

  const cookies = cookieServiceSSR.getAll();
  // Sync auth state between cookies and AuthProvider
  if (!!cookies['gs.ocs.auth'] != authService.isLoggedIn()) authService.isLoggedIn.set(!!cookies['gs.ocs.auth']);

  // Attach all cookies starting with 'gs.ocs.' to the request headers
  const gsHeaderKeyCookies = Object.entries(cookies).reduce(
    (acc: Record<string, string>, [k, v]) => {
      if (k.startsWith('gs.ocs.')) {
        acc[k] = v;
      }
      return acc;
    },
    {} as Record<string, string>
  );

  // Lazily resolve LanguageService to avoid circular dependency:
  // authInterceptor → LanguageService → TranslateService → HttpClient → authInterceptor
  const languageService = injector.get(LanguageService);

  // Fall back to the (validated) language cookie when currentLang isn't set yet (SSR / early bootstrap).
  const cookieLanguage = cookies[LANGUAGE_COOKIE_NAME];
  const validCookieLanguage = (languageService.supportedLangs as string[]).includes(cookieLanguage) ? cookieLanguage : undefined;
  const selectedLanguage = languageService.currentLang ?? validCookieLanguage;
  req = req.clone({
    setHeaders: {
      ...gsHeaderKeyCookies,
      ...(selectedLanguage ? { language: selectedLanguage } : {})
    }
  });

  return next(req).pipe(
    tap((res) => {
      if (!res || res.type === HttpEventType.Sent || !('headers' in res)) return res;
      const gsHeaderKeys = res.headers.keys().filter((k) => k.startsWith('gs.ocs.'));
      gsHeaderKeys.forEach((k) => {
        const v = res.headers.get(k);
        if (v) {
          cookieServiceSSR.set(k, v, { path: COOKIE_PATH, secure: true, sameSite: 'Lax' });
        } else {
          cookieServiceSSR.delete(k, COOKIE_PATH);
        }
      });
      return res;
    }),
    catchError((err: IHttpErrorResponse<unknown>) => {
      // The caller handles the error itself (no toast, no logout on 401)
      if (req.context.get(SKIP_ERROR_HANDLING)) return throwError(() => err);

      const translateService = injector.get(TranslateService);
      const isBrowser = (): boolean => systemService.browser;
      const copyErrorBtn = {
        text: translateService.instant('httpInterceptors.generic.copyFullError'),
        action: () => {
          navigator.clipboard.writeText(JSON.stringify(err)).then(
            () => toastService.showToast('', translateService.instant('httpInterceptors.generic.copiedToClipboard'), 'success', 2000),
            () => toastService.showToast('', translateService.instant('httpInterceptors.generic.copyFailed'), 'error', 2000)
          );
        }
      };

      // Handles unauthorized errors by clearing cookies and redirecting to login
      const handleUnauthorized = (): void => {
        cookieServiceSSR.deleteAll();
        setTimeout(() => {
          if (isBrowser()) router.navigate([initService.init()?.linkTargets.login ?? '/login']);
        }, 100);
      };

      // Handles too many requests errors by showing a specific toast message
      const handleTooManyRequests = (title?: string, message?: string): void => {
        if (!isBrowser()) return;
        toastService.showToast(
          title ?? translateService.instant('httpInterceptors.tooManyRequests.title'),
          message ?? translateService.instant('httpInterceptors.tooManyRequests.fallbackMessage'),
          'error',
          3000,
          { buttons: [copyErrorBtn] }
        );
      };

      // Handles form errors by extracting messages and showing them in a toast
      const handleFormError = (title?: string, message?: string): void => {
        if (!isBrowser()) return;
        toastService.showToast(
          title ?? translateService.instant('httpInterceptors.formError.title'),
          message ?? translateService.instant('httpInterceptors.formError.fallbackMessage'),
          'error',
          10000
        );
      };

      // Handles forbidden errors by showing a specific message
      const handleForbidden = (title?: string, message?: string): void => {
        if (!isBrowser()) return;
        toastService.showToast(
          title ?? translateService.instant('httpInterceptors.forbidden.title'),
          message ?? translateService.instant('httpInterceptors.forbidden.fallbackMessage'),
          'error',
          10000,
          { buttons: [copyErrorBtn] }
        );
      };

      // Handles client errors (400-series) by showing a generic error message
      const handleClientError = (title?: string, message?: string): void => {
        if (!isBrowser()) return;
        toastService.showToast(title ?? translateService.instant('httpInterceptors.generic.title'), message ?? '', 'error', 3000, {
          buttons: [copyErrorBtn]
        });
      };

      // Handles other HTTP errors by showing the status code and message
      const handleOtherHttpError = (title: string | undefined, message: string | undefined, httpErr: HttpErrorResponse): void => {
        if (!isBrowser()) return;
        toastService.showToast(title ?? '', `${httpErr.status} - ${message ?? httpErr.message}`, 'error', 3000, {
          buttons: [copyErrorBtn]
        });
      };

      // Handles non-HTTP errors by showing a generic error message
      const handleNonHttpError = (unknownErr: any): void => {
        if (!isBrowser()) return;
        const errorMessage = getMessageFromError(unknownErr);
        toastService.showToast(
          translateService.instant('httpInterceptors.generic.title'),
          errorMessage ?? translateService.instant('httpInterceptors.generic.unknown'),
          'error',
          3000,
          { buttons: [copyErrorBtn] }
        );
      };

      // Non-HTTP errors
      if (!(err instanceof HttpErrorResponse)) {
        console.error('An error occurred:', err);
        handleNonHttpError(err);
      }

      // HTTP errors
      const errorVal = DataProvider.mapCasesReverse(err.error) as unknown;
      const errorTitle = getTitleFromError(errorVal);
      const errorMessage = getMessageFromError(errorVal);

      if (err.status === 401) {
        // Unauthorized
        console.error('Unauthorized request:', err);
        handleUnauthorized();
      } else if (err.status === 403) {
        // Forbidden
        console.error('Forbidden request:', err);
        handleForbidden(errorTitle, errorMessage);
      } else if (err.status === 429) {
        // Too many requests
        console.error('Too many requests:', err);
        handleTooManyRequests(errorTitle, errorMessage);
      } else if (err.status === 400 && isIUpdateVarErrorsResult(errorVal) && errorVal.errors?.length > 0) {
        // 400 errors with validation issues in backend-dialog forms
        console.error('Error in dialog-form:', err);
        handleFormError(errorTitle, errorMessage);
      } else if (err.status >= 400 && err.status < 500) {
        // Other 400-series errors
        console.error('Error on request:', err);
        handleClientError(errorTitle, errorMessage);
      } else {
        // Other HTTP errors
        console.error('Error occurred:', err);
        handleOtherHttpError(errorTitle, errorMessage, err);
      }
      return throwError(() => err);
    })
  );
};
